import type { AppContext } from "../app/app";
import {
  addContactAsNew,
  addFreshContacts,
  isLinkedInExport,
  parseGenericContacts,
  parseLinkedInConnections,
  partitionImport,
  type Collision,
  type ContactCandidate,
  type GenericMapping,
} from "../data/contacts";
import { parseCsv } from "../data/csv";
import { el } from "./dom";

const HOWTO =
  "Opt in by importing a contacts CSV. For LinkedIn: Settings and Privacy > " +
  "Data privacy > Get a copy of your data > Connections, then import the " +
  "Connections.csv here. Name, company, the profile link and email are kept; " +
  "imported people are added untagged and stay private to this browser (email " +
  "is never sent to the model).";

// Opt-in contacts importer. New contacts import directly; any that already match
// someone in your list are resolved one at a time (same -> skip, different ->
// add), with "apply to all" for batching and "next" to defer. `compact` drops
// the cold-start card once there is already data.
export function renderImportPanel(ctx: AppContext, compact = false): HTMLElement {
  const status = el("span", { class: "remaining" });
  const stage = el("div", { class: "import__stage" });

  const finish = (msg: string) => {
    status.textContent = msg;
    status.classList.remove("is-over");
    stage.replaceChildren();
    ctx.refresh();
  };

  const beginImport = async (candidates: ContactCandidate[]) => {
    if (candidates.length === 0) {
      status.textContent = "No contacts found in that file.";
      status.classList.add("is-over");
      return;
    }
    const { fresh, collisions } = await partitionImport(ctx.repo, candidates);
    stage.replaceChildren(
      el(
        "div",
        { class: "import__confirm" },
        el("p", {
          class: "lede",
          text: `Found ${candidates.length} contacts: ${fresh.length} new, ${collisions.length} already in your list.`,
        }),
        el("button", {
          class: "btn btn--primary",
          text: fresh.length ? `Import ${fresh.length} new` : "Continue",
          on: {
            click: async () => {
              await addFreshContacts(ctx.repo, fresh);
              if (collisions.length) resolve(collisions, fresh.length);
              else finish(`Imported ${fresh.length} new.`);
            },
          },
        }),
      ),
    );
  };

  // Resolve collisions one at a time.
  const resolve = (collisions: Collision[], freshCount: number) => {
    const box = el("div", { class: "import__queue" });
    stage.replaceChildren(box);
    let queue = [...collisions];
    const tally = { skipped: 0, added: 0 };

    const render = () => {
      box.replaceChildren();
      if (queue.length === 0) {
        finish(
          `Imported ${freshCount} new, ${tally.added} added as separate, ${tally.skipped} skipped as the same.`,
        );
        return;
      }
      const cur = queue[0]!;
      const applyAll = el("input", { type: "checkbox" });
      const done = collisions.length - queue.length + 1;

      const skip = () => {
        if (applyAll.checked) {
          tally.skipped += queue.length;
          queue = [];
        } else {
          tally.skipped++;
          queue.shift();
        }
        render();
      };
      const addNew = async () => {
        if (applyAll.checked) {
          for (const c of queue) await addContactAsNew(ctx.repo, c.candidate);
          tally.added += queue.length;
          queue = [];
        } else {
          await addContactAsNew(ctx.repo, cur.candidate);
          tally.added++;
          queue.shift();
        }
        render();
      };
      const next = () => {
        queue.push(queue.shift()!);
        render();
      };

      const emailBit = cur.candidate.email ? `, ${cur.candidate.email}` : "";
      box.append(
        el("span", { class: "label", text: `Already in your list — ${done} of ${collisions.length}` }),
        el("p", {
          class: "import__q",
          text:
            `Import "${cur.candidate.display_name}" (${cur.candidate.relationship_context || "no company"}${emailBit}). ` +
            `You already have "${cur.existing.display_name}" (${cur.existing.relationship_context || "no context"}). Same person?`,
        }),
        el(
          "div",
          { class: "import__queue-actions" },
          el("button", { class: "btn btn--ghost", text: "Same, skip", on: { click: skip } }),
          el("button", { class: "btn btn--primary", text: "Different, add", on: { click: () => void addNew() } }),
          el("button", { class: "btn btn--ghost", text: "Next", on: { click: next } }),
        ),
        el("label", { class: "import__applyall" }, applyAll, el("span", { text: "Apply to all remaining" })),
      );
    };
    render();
  };

  const fileInput = el("input", { type: "file" });
  fileInput.accept = ".csv,text/csv";
  fileInput.style.display = "none";
  fileInput.addEventListener("change", async () => {
    const f = fileInput.files?.[0];
    if (!f) return;
    stage.replaceChildren();
    status.textContent = "";
    status.classList.remove("is-over");
    try {
      const text = await f.text();
      if (isLinkedInExport(text)) await beginImport(parseLinkedInConnections(text));
      else stage.append(mappingStage(text, beginImport, status));
    } catch (e) {
      status.textContent = e instanceof Error ? e.message : "Could not read that file.";
      status.classList.add("is-over");
    } finally {
      fileInput.value = "";
    }
  });

  const choose = el("button", {
    class: "btn btn--ghost",
    text: "Import contacts (CSV)",
    on: { click: () => fileInput.click() },
  });

  if (compact) {
    return el(
      "div",
      { class: "import import--compact" },
      el("div", { class: "settings__actions" }, choose, fileInput, status),
      stage,
    );
  }

  return el(
    "div",
    { class: "import" },
    el("span", { class: "label", text: "Cold start" }),
    el("p", { class: "lede data-note", text: HOWTO }),
    el("div", { class: "settings__actions" }, choose, fileInput, status),
    stage,
  );
}

function columnSelect(header: string[], includeNone: boolean): HTMLSelectElement {
  const sel = el("select", { class: "input" });
  if (includeNone) sel.append(el("option", { value: "-1", text: "(none)" }));
  header.forEach((h, i) =>
    sel.append(el("option", { value: String(i), text: h || `Column ${i + 1}` })),
  );
  return sel;
}

function autoPick(header: string[], sel: HTMLSelectElement, re: RegExp): void {
  const i = header.findIndex((h) => re.test(h));
  if (i >= 0) sel.value = String(i);
}

function mappingStage(
  text: string,
  begin: (c: ContactCandidate[]) => void,
  status: HTMLElement,
): HTMLElement {
  const header = parseCsv(text)[0] ?? [];
  const nameSel = columnSelect(header, false);
  const companySel = columnSelect(header, true);
  const positionSel = columnSelect(header, true);
  autoPick(header, nameSel, /name/i);
  autoPick(header, companySel, /company|org/i);
  autoPick(header, positionSel, /position|role|title/i);

  const idx = (sel: HTMLSelectElement) => {
    const v = parseInt(sel.value, 10);
    return v >= 0 ? v : undefined;
  };
  const field = (label: string, control: HTMLElement) =>
    el("label", { class: "import__field" }, el("span", { class: "label", text: label }), control);

  const importBtn = el("button", {
    class: "btn btn--primary",
    text: "Continue",
    on: {
      click: () => {
        const name = parseInt(nameSel.value, 10);
        if (!(name >= 0)) {
          status.textContent = "Choose the name column.";
          status.classList.add("is-over");
          return;
        }
        const mapping: GenericMapping = { name, company: idx(companySel), position: idx(positionSel) };
        begin(parseGenericContacts(text, mapping));
      },
    },
  });

  return el(
    "div",
    { class: "import__map" },
    el("p", { class: "lede", text: "Not a LinkedIn export. Map your columns:" }),
    el(
      "div",
      { class: "import__fields" },
      field("Name", nameSel),
      field("Company", companySel),
      field("Position", positionSel),
    ),
    importBtn,
  );
}
